import React, {useMemo} from "react";
import {BackOrdersListsItemsDataGridCellKeys, BackOrdersListsOrdersDataGridCellKeys} from "../../../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes} from "../../../../../../../../../packages/data-grid";
import IconButton from "../../../../../../../../components/base/button/icon_button";
import {OpenInNew} from "@mui/icons-material";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../../../containers/data-grid";
import {CircularProgress, SvgIcon} from "@mui/material";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import ProductInformationPopover from "../../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {ReactComponent as Printer} from "../../../../../../../../../assets/images/printer.svg";
import Button from "../../../../../../../../components/base/button/button";


const BackOrdersListViewItemsTable = ({
                                          searchData,
                                          exportToCsv,
                                          data,
                                          dataGridSaveConfig,
                                          fetchingOrdersCustomer,
                                          navigateToInformationView,
                                          navigateToPrintView,
                                          revertToQuote,
                                      }) => {
    const operatorAccessLevels = useAccessLevels();

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.salesOrderInformation ? [
                {
                    name: BackOrdersListsItemsDataGridCellKeys.navigate,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
                ...(operatorAccessLevels.packages.bizPrint.printSalesOrder ? [
                    {
                        name: BackOrdersListsItemsDataGridCellKeys.print,
                        alignment: DataGridColumnAlignments.center,
                        type: DataGridColumnTypes.element,
                        pinned: true,
                        sortable: false,
                        pinnedToggleable: false,
                        resizable: false,
                        visibilityToggleable: false,
                        width: 0,
                    },
                ] : []),
            ] : []),
            {
                title: "Order No",
                name: BackOrdersListsItemsDataGridCellKeys.orderNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: true,
            },
            {
                title: "Customer",
                name: BackOrdersListsItemsDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Part No",
                name: BackOrdersListsItemsDataGridCellKeys.partNo,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
            },
            {
                title: "Order Date",
                name: BackOrdersListsItemsDataGridCellKeys.submitDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Unit Price",
                name: BackOrdersListsItemsDataGridCellKeys.unitPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
            },
            {
                title: "Order Qty",
                name: BackOrdersListsItemsDataGridCellKeys.orderQty,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.number,
            },
            ...(operatorAccessLevels.packages.sales.manageSalesOrders ? [
                {
                    name: BackOrdersListsItemsDataGridCellKeys.revertToQuote,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    pinnedType: DataGridColumnPinnedTypes.right,
                    pinnedToggleable: false,
                    sortable: false,
                    reorderable: false,
                    visibilityToggleable: false,
                    width: 200,
                },
            ] : []),
        ], [operatorAccessLevels.packages.bizPrint.printSalesOrder, operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.sales.salesOrderInformation])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [BackOrdersListsItemsDataGridCellKeys.navigate]:
                    <IconButton
                        color={'secondary'}
                        disabled={fetchingOrdersCustomer.includes(item.orderId)}
                        onClick={() => navigateToInformationView(item.customer, item.orderId)}
                    >
                        {
                            fetchingOrdersCustomer.includes(item.orderId)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <OpenInNew className='icon normal'/>
                        }
                    </IconButton>,
                [BackOrdersListsItemsDataGridCellKeys.print]:
                    <IconButton
                        disabled={fetchingOrdersCustomer.includes(item.orderId)}
                        color={'paper.secondary'}
                        onClick={() => navigateToPrintView(item.customer, item.orderId)}
                    >
                        {
                            fetchingOrdersCustomer.includes(item.orderId)
                                ? <CircularProgress size={20} color={'secondary'}/>
                                : <SvgIcon className='icon medium' component={Printer}/>
                        }
                    </IconButton>,
                [BackOrdersListsItemsDataGridCellKeys.orderNo]: item.orderNo,
                [BackOrdersListsItemsDataGridCellKeys.customer]: item.customer?.name,
                [BackOrdersListsItemsDataGridCellKeys.partNo]:
                    <ProductInformationPopover partNo={item.partNo}>
                        {item.partNo ?? ""}
                    </ProductInformationPopover>,
                [BackOrdersListsItemsDataGridCellKeys.submitDate]: item.createdDate,
                [BackOrdersListsItemsDataGridCellKeys.unitPrice]: item.unitPrice,
                [BackOrdersListsItemsDataGridCellKeys.orderQty]: item.orderQty,
                [BackOrdersListsOrdersDataGridCellKeys.revertToQuote]: item?.isClosed
                    ? null
                    : <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={item.loading || fetchingOrdersCustomer.includes(item.orderId)}
                        onClick={() => revertToQuote(item.customer, item.orderId)}
                    >
                        {
                            item.loading
                                ? 'loading...'
                                : "Revert to Quote"
                        }
                    </Button>
            }
        }))
    }, [data, fetchingOrdersCustomer, navigateToInformationView, navigateToPrintView, revertToQuote])

    return (
        <>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                getExportInfo={exportToCsv}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}
export default BackOrdersListViewItemsTable;
